<script setup lang="ts">
import DashboardLayout from '@/onenetwork/layouts/DashboardLayout.vue'
import DataTable from '@/onenetwork/components/datatable/DataTable.vue'
import FilterList from '@/onenetwork/components/filter/FilterList.vue'
import { useRouter } from 'vue-router'
import { computed, onMounted, ref, watch } from 'vue'
import _ from 'lodash'
import { useConfirm } from 'primevue'
import {
  ListingListActionTypes,
  useListingListStore,
} from '@/modules/realestate/stores/listing.list.store.ts'
import {
  useListingDetailStore,
} from '@/modules/realestate/stores/listing.detail.store.ts'
import ListingDetail from '@/modules/realestate/views/ListingDetail.vue'
import { useReactiveQueryParam, useUpdateUrlParam } from '@/composables/useReactiveQueryParam'

const router = useRouter()
const confirm = useConfirm()
const listingListStore = useListingListStore()
const listingDetailStore = useListingDetailStore()
const idsSelected = ref<Array<string | number>>([])
const pageTabs = computed(() => {
  return listingListStore.tabs
})
const displayTabContent = computed(() => {
  return listingListStore.tabs?.find((tab) => tab.active)?.id || 'list'
})
const TableConfig = computed(() => {
  return listingListStore.tableConfig
})
const filter = computed(() => {
  return listingListStore.filter
})
const loading = computed(() => {
  return listingListStore.action_type == ListingListActionTypes.FETCHING
})
const page = computed(() => {
  return listingListStore.page
})
const pageSize = computed(() => {
  return listingListStore.pageSize
})
const dataSource = computed(() => {
  return listingListStore.data
})

const activeTab = computed(() => {
  return listingListStore.tabs.find((tab) => tab.active)
})
const detailId: any = useReactiveQueryParam('detailId')
const { updateUrlParam } = useUpdateUrlParam()

const setDetailTab = async () => {
  if(parseInt(detailId.value || 0)){
    try {
      const data: any = await listingDetailStore.fetchListingDetail(detailId.value);
      listingListStore.newTab({
        id: data.id,
        label: `#${data.id} - ${data?.title}`,
        active: true,
      })
    } catch (e: any) {
      console.log(e)
    }
  }
}

const handleChangeTab = (tab: any) => {
  listingListStore.changeTab(tab.id)
  updateUrlParam('detailId', activeTab.value?.id !== 'list' ? activeTab.value?.id : null)
}

const handleCloseTab = (tab: any) => {
  listingListStore.closeTab(tab.id)
  updateUrlParam('detailId', activeTab.value?.id !== 'list' ? activeTab.value?.id : null)
  listingDetailStore.removeListingDetail(tab.id)
}

const handleCloseAllTab = () => {
  listingListStore.closeAllTab()
  updateUrlParam('detailId', activeTab.value?.id !== 'list' ? activeTab.value?.id : null)
  listingDetailStore.removeAllListingDetails()
}

const handleChangePage = (page: number) => {
  listingListStore.changePage(page)
}

const handleChangePageSize = (size: number) => {
  listingListStore.changePageSize(size)
}

const handleUpdateFilter = (filter: any) => {
  listingListStore.setFilter(filter)
}

const handleRowDblClick = (e: MouseEvent, item: any) => {
  // console.log(e, item)
  listingListStore.newTab({
    id: item.code,
    label: `#${item.code} - ${item.subject?.title}`,
    active: true,
  })
  updateUrlParam('detailId', activeTab.value?.id !== 'list' ? activeTab.value?.id : null)
}

const handleChangeSelectedRow = (ids: Array<string | number>) => {
  idsSelected.value = ids
}

const handleSort = (field: string, direction: string) => {
  listingListStore.toggleSort(field, direction)
}

const requestFetchListingList = _.debounce(async () => {
  try {
    await listingListStore.fetchList()
  } catch (e) {}
}, 500)

watch(
  () => listingListStore.uuid,
  (newVal, oldVal) => {
    requestFetchListingList()
  },
)

onMounted(() => {
  listingListStore.changeTab('list')
  if (listingListStore.action_type === ListingListActionTypes.PENDING) requestFetchListingList()
  if (detailId) setDetailTab()
})

const requestDelete = async () => {
  try {
    await listingListStore.deleteListing(idsSelected.value)
    requestFetchListingList()
  } catch (e: any) {
    console.error('Error deleting listing:', e)
  }
}

const handleDelete = async () => {
  if (idsSelected.value.length === 0) {
    return
  }
  confirm.require({
    message: 'Bạn có muốn xoá tin đăng này không?',
    header: 'Xác nhận',
    // icon: 'pi pi-exclamation-triangle',
    rejectProps: {
      label: 'Huỷ',
      severity: 'secondary',
      outlined: true,
    },
    acceptProps: {
      label: 'Đồng ý',
    },
    accept: () => {
      requestDelete()
    },
    reject: () => {
      // toast.add({ severity: 'error', summary: 'Rejected', detail: 'You have rejected', life: 3000 });
    },
  })
}
</script>

<template>
  <dashboard-layout
    currentRoute="property/listings"
    page-title="Danh sách tin đăng BDS"
    :tabs="pageTabs"
    :tabsEvent="{
      close: handleCloseTab,
      select: handleChangeTab,
      closeAll: handleCloseAllTab,
    }"
  >
    <template v-if="displayTabContent == 'list'">
      <filter-list :filter="filter" @update:filter="handleUpdateFilter" />
      <data-table
        :loading="loading"
        :tableConfig="TableConfig"
        :page-size="pageSize"
        :page="page"
        :total-page="dataSource?.totalPage || 0"
        :total-records="dataSource?.totalRecords || 0"
        :data="dataSource?.list || []"
        :row-action-buttons="[
          {
            action: handleRowDblClick,
            css: 'btn btn-cs',
            label: 'Chi tiết',
            icon: 'iconamoon:edit-duotone',
            perm: 'real_estate.detail',
          },
        ]"
        :buttons="[
          {
            label: 'Tạo tin mới',
            icon: 'gridicons:create',
            class: 'btn bg-primary bg-hover-primary text-white',
            action: () => {
              router.push({ name: 'property-listings-create' })
            },
            perm: 'real_estate.create'
          },

          {
            label: 'Xoá tin',
            icon: 'gridicons:create',
            class: 'btn btn-danger text-white !border-0',
            action: handleDelete,
            perm: 'real_estate.delete'
          },
        ]"
        primaryField="code"
        @page-changed="handleChangePage"
        @pagesize-changed="handleChangePageSize"
        @toggle-visibility="listingListStore.toggleColumnVisibility"
        @on-selected-row="handleChangeSelectedRow"
        @on-sort="handleSort"
      />
    </template>
    <template v-else>
      <template v-for="tab in pageTabs">
        <template v-if="tab.id == displayTabContent">
          <ListingDetail :id="tab.id" :key="tab.id" :reloadList="requestFetchListingList" />
        </template>
      </template>
    </template>
  </dashboard-layout>
</template>

<style scoped></style>
